/**
 * Notification Runtime Orchestrator
 *
 * Bridges notification templates to email delivery by:
 *   1. Loading active notification templates from Medplum
 *   2. Matching templates by trigger resource type
 *   3. Evaluating trigger conditions
 *   4. Resolving recipients
 *   5. Rendering subject/body with runtime variable values
 *   6. Creating a send-email-notification Task (picked up by notification-email bot)
 *   7. Recording audit artifacts (CommunicationRequest, AuditEvent)
 *
 * This runtime is invoked either:
 *   - By a Rules Engine hook that detects resource changes
 *   - By a Medplum Subscription on relevant resource types
 *   - Manually via the "Send test email" UI flow
 */
import type { MedplumClient } from '@medplum/core';
import type { ActivityDefinition, AuditEvent, Task } from '@medplum/fhirtypes';
import { executeTaskMutation } from '../../../../lib/taskLifecycleBots';

import type { NotificationTemplateConfig } from '../notification-template.types';
import type {
  NotificationEventContext,
  NotificationAuditOutcome,
  EmailSendInput,
  NotificationTemplateAutomationConfig,
} from '../notification-automation.types';

type TemplateConfigWithAutomation = NotificationTemplateConfig & {
  automation?: NotificationTemplateAutomationConfig;
};
import {
  NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  NOTIFICATION_AD_URL_PREFIX,
  NOTIFICATION_AUDIT_CODE_SYSTEM,
  NOTIFICATION_DEDUPE_KEY_SYSTEM,
} from '../notification-template.constants';
import { getJsonExtension } from '../notification-template.medplum';
import { evaluateTrigger } from './notification-trigger-evaluator';
import { resolveAndDedupeRecipientsAsync, buildDedupeKey } from './notification-recipient-resolver';
import { renderTemplateString } from './notification-template-renderer';
import { resolveAllVariableValues } from './notification-variable-resolver';
import {
  findProfileByEmail,
  preferenceCategoryForTriggerResourceType,
} from '@/lib/notificationDeliveryGuards';
import { isNotificationEnabled } from '@/lib/notificationPreferences';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TASK_CODE_SYSTEM = 'https://unify.health/fhir/task-type';
const TASK_CODE_SEND_EMAIL = 'send-email-notification';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotificationRuntimeResult {
  templateId: string;
  templateName: string;
  recipientsSent: string[];
  recipientsSkipped: Array<{ email: string; reason: string }>;
  auditOutcomes: NotificationAuditOutcome[];
  tasksCreated: string[];
  errors: string[];
}

export interface NotificationExecutionResult {
  results: NotificationRuntimeResult[];
  errors: string[];
}

export interface SendTestEmailInput {
  templateConfig: NotificationTemplateConfig;
  recipientEmail: string;
  sampleContext?: Record<string, string>;
  isTest: boolean;
}

// ---------------------------------------------------------------------------
// Core runtime
// ---------------------------------------------------------------------------

/**
 * @deprecated Automation trigger/condition/recipient evaluation is now owned
 * by Rules Engine. This function remains for backward compatibility but
 * production email delivery should use Rules Engine → shared
 * notification-email-task-builder path. Use sendTestEmail() for test sends.
 */
export async function executeNotificationTemplates(
  medplum: MedplumClient,
  event: NotificationEventContext,
): Promise<NotificationExecutionResult> {
  const results: NotificationRuntimeResult[] = [];
  const errors: string[] = [];

  let templates: ActivityDefinition[];
  try {
    templates = await loadActiveNotificationTemplates(medplum);
  } catch (err) {
    return { results: [], errors: [`Failed to load templates: ${String(err)}`] };
  }

  for (const ad of templates) {
    const config = getJsonExtension<NotificationTemplateConfig>(
      ad,
      NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
    );
    if (!config) continue;

    const configWithAuto = config as TemplateConfigWithAutomation;
    if (!isTemplateEligible(configWithAuto, event.resourceType)) continue;

    const triggerMatches = evaluateTrigger(configWithAuto.automation!.trigger, {
      changeType: configWithAuto.automation!.trigger.changeType,
      currentResource: event.currentResource,
      previousResource: event.previousResource,
      isNew: !event.previousResource,
    });

    if (!triggerMatches) continue;

    const result = await executeTemplateForEvent(medplum, configWithAuto, event);
    results.push(result);
  }

  return { results, errors };
}

/**
 * Execute a single template for a matched event.
 */
async function executeTemplateForEvent(
  medplum: MedplumClient,
  config: TemplateConfigWithAutomation,
  event: NotificationEventContext,
): Promise<NotificationRuntimeResult> {
  const result: NotificationRuntimeResult = {
    templateId: config.id,
    templateName: config.name,
    recipientsSent: [],
    recipientsSkipped: [],
    auditOutcomes: [],
    tasksCreated: [],
    errors: [],
  };

  const automation = config.automation!;

  // 1. Resolve recipients
  const { valid: validRecipients, invalid: invalidRecipients } =
    await resolveAndDedupeRecipientsAsync(
      automation.recipients,
      event.currentResource,
      medplum,
    );

  for (const inv of invalidRecipients) {
    result.recipientsSkipped.push({ email: inv.email || '(unresolvable)', reason: inv.reason || 'unknown' });
    result.auditOutcomes.push(inv.email ? 'invalid-recipient' : 'no-recipient');
    await createNotificationAuditEvent(medplum, config, event, inv.email ? 'invalid-recipient' : 'no-recipient', {
      recipientConfigId: inv.configId,
      reason: inv.reason,
    });
  }

  if (validRecipients.length === 0) {
    result.errors.push('No valid recipients resolved');
    return result;
  }

  // 2. Resolve runtime variable values (every {{token}} in subject/body, not only Custom Variables rows)
  const variableValues = await resolveAllVariableValues(config, event, medplum);

  // 3. Render subject/body
  const subjectResult = renderTemplateString(config.subject, config.variables, variableValues);
  const htmlBodyResult = renderTemplateString(config.htmlBody, config.variables, variableValues);
  const textBodyResult = renderTemplateString(
    config.textBody ?? '',
    config.variables,
    variableValues,
  );

  // Check required variables
  const allUnresolved = [
    ...new Set([
      ...subjectResult.unresolvedRequired,
      ...htmlBodyResult.unresolvedRequired,
      ...textBodyResult.unresolvedRequired,
    ]),
  ];

  if (allUnresolved.length > 0) {
    result.errors.push(`Missing required variables: ${allUnresolved.join(', ')}`);
    result.auditOutcomes.push('missing-required-variable');
    await createNotificationAuditEvent(medplum, config, event, 'missing-required-variable', {
      missingVariables: allUnresolved,
    });
    return result;
  }

  // 4. Resolve sender
  const senderEmail = automation.sender?.email || config.from?.email || 'noreply@unifymentalhealth.com';
  const senderName = automation.sender?.name || config.from?.name || 'Unify Mental Health';

  // 5. For each valid recipient, create a send-email Task
  for (const recipient of validRecipients) {
    const category = preferenceCategoryForTriggerResourceType(event.resourceType);
    const resolvedProfile = await findProfileByEmail(medplum, recipient.email);
    if (resolvedProfile && !isNotificationEnabled(resolvedProfile.profile, resolvedProfile.role, category, 'email')) {
      result.recipientsSkipped.push({
        email: recipient.email,
        reason: 'notification-preferences-email-disabled',
      });
      result.auditOutcomes.push('suppressed-preferences');
      await createNotificationAuditEvent(medplum, config, event, 'suppressed-preferences', {
        recipientEmail: recipient.email,
        preferenceCategory: category,
      });
      continue;
    }

    const dedupeKey = buildDedupeKey(
      event.resourceType,
      event.resourceId,
      config.id,
      config.version,
      recipient.email,
    );

    try {
      const task = await createSendEmailTask(medplum, {
        from: { email: senderEmail, name: senderName },
        to: recipient.email,
        subject: subjectResult.rendered,
        htmlBody: htmlBodyResult.rendered,
        textBody: textBodyResult.rendered || undefined,
        idempotencyKey: dedupeKey,
      }, config, event);

      result.recipientsSent.push(recipient.email);
      result.tasksCreated.push(task.id ?? '');
      result.auditOutcomes.push('queued');
    } catch (err) {
      result.recipientsSkipped.push({ email: recipient.email, reason: String(err) });
      result.auditOutcomes.push('failed');
      result.errors.push(`Failed to create Task for ${recipient.email}: ${String(err)}`);
    }
  }

  return result;
}

/**
 * Send a test email using the shared notification-email-task-builder service.
 * Delegates to the same render/task-creation path used by Rules Engine production emails.
 */
export async function sendTestEmail(
  medplum: MedplumClient,
  input: SendTestEmailInput,
): Promise<{ ok: boolean; taskId?: string; error?: string }> {
  const { sendTestEmail: sharedSendTest } = await import('./notification-email-task-builder');
  const result = await sharedSendTest(medplum, {
    templateConfig: input.templateConfig,
    recipientEmail: input.recipientEmail,
    sampleContext: input.sampleContext,
  });
  return { ok: result.ok, taskId: result.taskId, error: result.error };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isTemplateEligible(config: TemplateConfigWithAutomation, eventResourceType: string): boolean {
  return (
    config.channel === 'email' &&
    config.status === 'active' &&
    Boolean(config.automation) &&
    config.automation!.trigger.resourceType === eventResourceType
  );
}

async function loadActiveNotificationTemplates(
  medplum: MedplumClient,
): Promise<ActivityDefinition[]> {
  const bundle = await medplum.search('ActivityDefinition', {
    status: 'active',
    _sort: '-_lastUpdated',
    _count: '200',
  });
  return (bundle.entry ?? [])
    .map((e) => e.resource as ActivityDefinition | undefined)
    .filter((r): r is ActivityDefinition =>
      Boolean(r?.url?.startsWith(NOTIFICATION_AD_URL_PREFIX)),
    );
}

async function createSendEmailTask(
  medplum: MedplumClient,
  emailInput: EmailSendInput,
  config: NotificationTemplateConfig,
  event?: NotificationEventContext,
  isTest?: boolean,
): Promise<Task> {
  const taskInput: Task['input'] = [
    { type: { text: 'to' }, valueString: emailInput.to },
    { type: { text: 'subject' }, valueString: emailInput.subject },
    { type: { text: 'body' }, valueString: emailInput.htmlBody },
  ];

  if (emailInput.textBody) {
    taskInput.push({ type: { text: 'textBody' }, valueString: emailInput.textBody });
  }

  const fromEmail = emailInput.from?.email?.trim();
  if (fromEmail) {
    taskInput.push({ type: { text: 'from' }, valueString: fromEmail });
    const fromName = emailInput.from?.name?.trim();
    if (fromName) {
      taskInput.push({ type: { text: 'fromName' }, valueString: fromName });
    }
  }

  const identifier = emailInput.idempotencyKey
    ? [{ system: NOTIFICATION_DEDUPE_KEY_SYSTEM, value: emailInput.idempotencyKey }]
    : undefined;

  const task: Task = {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    code: {
      coding: [{ system: TASK_CODE_SYSTEM, code: TASK_CODE_SEND_EMAIL }],
    },
    description: `Send email: "${config.name}" to ${emailInput.to}${isTest ? ' [TEST]' : ''}`,
    input: taskInput,
    identifier,
    meta: {
      tag: [
        { system: NOTIFICATION_AUDIT_CODE_SYSTEM, code: `template/${config.id}` },
        { system: NOTIFICATION_AUDIT_CODE_SYSTEM, code: `version/${config.version}` },
        ...(isTest ? [{ system: NOTIFICATION_AUDIT_CODE_SYSTEM, code: 'test-send' }] : []),
        ...(event
          ? [{ system: NOTIFICATION_AUDIT_CODE_SYSTEM, code: `trigger/${event.resourceType}/${event.resourceId}` }]
          : []),
      ],
    },
  };

  return executeTaskMutation(medplum, { action: 'create-payload', task });
}

async function createNotificationAuditEvent(
  medplum: MedplumClient,
  config: NotificationTemplateConfig,
  event: NotificationEventContext,
  outcome: NotificationAuditOutcome,
  details?: Record<string, unknown>,
): Promise<AuditEvent> {
  return medplum.createResource<AuditEvent>({
    resourceType: 'AuditEvent',
    type: {
      system: 'http://dicom.nema.org/resources/ontology/DCM',
      code: '110150',
      display: 'Application Activity',
    },
    subtype: [{ system: NOTIFICATION_AUDIT_CODE_SYSTEM, code: outcome }],
    action: 'E',
    recorded: new Date().toISOString(),
    outcome: outcome === 'queued' || outcome === 'sent' ? '0' : '8',
    outcomeDesc: JSON.stringify({
      templateId: config.id,
      templateVersion: config.version,
      templateName: config.name,
      triggerResource: `${event.resourceType}/${event.resourceId}`,
      outcome,
      ...details,
    }),
    agent: [{ requestor: true, who: { display: 'notification-runtime' } }],
    source: {
      observer: { display: 'notification-runtime' },
    },
    entity: [
      { what: { reference: `${event.resourceType}/${event.resourceId}` } },
    ],
  });
}
